import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  Badge,
  Avatar,
  Tooltip,
  IconButton,
  Menu,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  Slider,
  CircularProgress,
} from '@mui/material';
import {
  Analytics,
  TrendingUp,
  TrendingDown,
  LocationOn,
  Schedule,
  Speed,
  LocalTaxi,
  People,
  AttachMoney,
  Insights,
  PsychologyAlt,
  AutoGraph,
  ShowChart,
  Map,
  Notifications,
  Settings,
  FilterList,
  Download,
  Refresh,
  Search,
  Add,
  Edit,
  MoreVert,
  Timeline,
  Assessment,
  Traffic,
  Event,
  WbSunny,
  Thunderstorm,
  CloudQueue,
  SportsSoccer,
  MusicNote,
  Business,
  School,
  Restaurant,
  ShoppingCart,
  Flight,
  Train,
  DirectionsBus,
  Warning,
  CheckCircle,
  Error,
  Info,
  Equalizer,
  PieChart,
  BarChart,
  ScatterPlot,
  BubbleChart,
  MultilineChart,
  CandlestickChart,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, AreaChart, Area, BarChart as RechartsBarChart, Bar, PieChart as RechartsPieChart, Pie, Cell, ComposedChart, ScatterChart, Scatter, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';

interface DemandMetric {
  id: string;
  title: string;
  currentValue: number;
  previousValue: number;
  unit: string;
  change: number;
  changeDirection: 'up' | 'down';
  status: 'high' | 'normal' | 'low';
  forecast: number;
  confidence: number;
  icon: React.ReactNode;
  color: string;
}

interface DemandForecast {
  timestamp: string;
  actual?: number;
  predicted: number;
  confidence_upper: number;
  confidence_lower: number;
  events?: string[];
}

interface HotspotArea {
  id: string;
  name: string;
  location: { lat: number; lng: number; };
  currentDemand: number;
  predictedDemand: number;
  supplyAvailable: number;
  utilizationRate: number;
  averageWaitTime: number;
  peakHours: string[];
  demandDrivers: string[];
  priceMultiplier: number;
  trend: 'increasing' | 'decreasing' | 'stable';
  category: 'airport' | 'business' | 'residential' | 'entertainment' | 'shopping' | 'transport';
}

interface EventImpact {
  id: string;
  name: string;
  type: 'sports' | 'concert' | 'conference' | 'weather' | 'holiday' | 'transport';
  date: string;
  location: string;
  expectedAttendance: number;
  demandImpact: number;
  priceImpact: number;
  durationHours: number;
  status: 'upcoming' | 'active' | 'completed';
  historicalData?: {
    previousImpact: number;
    accuracy: number;
  };
}

interface PricingRecommendation {
  id: string;
  area: string;
  currentPrice: number;
  recommendedPrice: number;
  confidence: number;
  reasoning: string[];
  expectedImpact: {
    demandChange: number;
    revenueChange: number;
    utilizationChange: number;
  };
  validUntil: string;
  status: 'active' | 'implemented' | 'rejected';
}

interface SupplyOptimization {
  id: string;
  area: string;
  currentSupply: number;
  optimalSupply: number;
  gap: number;
  gapType: 'shortage' | 'oversupply' | 'balanced';
  recommendations: string[];
  impact: {
    waitTimeReduction: number;
    utilizationImprovement: number;
    revenueIncrease: number;
  };
  priority: 'high' | 'medium' | 'low';
}

interface MarketInsight {
  id: string;
  title: string;
  category: 'demand-pattern' | 'pricing' | 'competition' | 'seasonal' | 'behavioral';
  insight: string;
  impact: 'high' | 'medium' | 'low';
  actionable: boolean;
  data: {
    metric: string;
    value: number;
    change: number;
  };
  recommendations: string[];
  confidence: number;
}

const DemandIntelligencePage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [timeRange, setTimeRange] = useState('24h');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [forecastHorizon, setForecastHorizon] = useState(7);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [realTimeUpdates, setRealTimeUpdates] = useState(true);

  // Sample data - in real implementation, this would come from ML models and APIs
  const demandMetrics: DemandMetric[] = [
    {
      id: 'current-demand',
      title: 'Current Demand',
      currentValue: 2847,
      previousValue: 2654,
      unit: 'requests/hour',
      change: 7.3,
      changeDirection: 'up',
      status: 'high',
      forecast: 3240,
      confidence: 0.87,
      icon: <LocalTaxi />,
      color: '#2196F3'
    },
    {
      id: 'supply-utilization',
      title: 'Supply Utilization',
      currentValue: 78.5,
      previousValue: 82.1,
      unit: '%',
      change: -4.4,
      changeDirection: 'down',
      status: 'normal',
      forecast: 85.2,
      confidence: 0.92,
      icon: <Speed />,
      color: '#4CAF50'
    },
    {
      id: 'avg-wait-time',
      title: 'Average Wait Time',
      currentValue: 4.2,
      previousValue: 3.8,
      unit: 'minutes',
      change: 10.5,
      changeDirection: 'up',
      status: 'high',
      forecast: 3.5,
      confidence: 0.79,
      icon: <Schedule />,
      color: '#FF9800'
    },
    {
      id: 'revenue-per-hour',
      title: 'Revenue per Hour',
      currentValue: 125400,
      previousValue: 118900,
      unit: 'NGN',
      change: 5.5,
      changeDirection: 'up',
      status: 'high',
      forecast: 142300,
      confidence: 0.84,
      icon: <AttachMoney />,
      color: '#9C27B0'
    }
  ];

  const demandForecastData: DemandForecast[] = [
    { timestamp: '00:00', actual: 1200, predicted: 1180, confidence_upper: 1250, confidence_lower: 1100 },
    { timestamp: '04:00', actual: 800, predicted: 820, confidence_upper: 900, confidence_lower: 740 },
    { timestamp: '08:00', actual: 2400, predicted: 2380, confidence_upper: 2500, confidence_lower: 2250, events: ['Morning Rush'] },
    { timestamp: '12:00', actual: 1800, predicted: 1850, confidence_upper: 1950, confidence_lower: 1750 },
    { timestamp: '16:00', actual: 2200, predicted: 2150, confidence_upper: 2300, confidence_lower: 2000 },
    { timestamp: '20:00', actual: 2800, predicted: 2900, confidence_upper: 3100, confidence_lower: 2700, events: ['Evening Peak'] },
    { timestamp: '24:00', predicted: 1600, confidence_upper: 1750, confidence_lower: 1450 },
    { timestamp: '28:00', predicted: 2400, confidence_upper: 2600, confidence_lower: 2200, events: ['Predicted Morning Rush'] },
  ];

  const hotspotAreas: HotspotArea[] = [
    {
      id: '1',
      name: 'Lagos Island Business District',
      location: { lat: 6.4531, lng: 3.3958 },
      currentDemand: 340,
      predictedDemand: 420,
      supplyAvailable: 180,
      utilizationRate: 89.2,
      averageWaitTime: 2.3,
      peakHours: ['08:00-10:00', '17:00-19:00'],
      demandDrivers: ['Business meetings', 'Banking', 'Corporate events'],
      priceMultiplier: 1.8,
      trend: 'increasing',
      category: 'business'
    },
    {
      id: '2',
      name: 'Murtala Muhammed Airport',
      location: { lat: 6.5774, lng: 3.3211 },
      currentDemand: 280,
      predictedDemand: 310,
      supplyAvailable: 95,
      utilizationRate: 74.6,
      averageWaitTime: 5.8,
      peakHours: ['06:00-08:00', '14:00-16:00', '20:00-22:00'],
      demandDrivers: ['Flight arrivals', 'Departures', 'Airport staff'],
      priceMultiplier: 1.5,
      trend: 'stable',
      category: 'airport'
    },
    {
      id: '3',
      name: 'Victoria Island Entertainment',
      location: { lat: 6.4281, lng: 3.4219 },
      currentDemand: 195,
      predictedDemand: 450,
      supplyAvailable: 140,
      utilizationRate: 65.3,
      averageWaitTime: 3.7,
      peakHours: ['19:00-02:00'],
      demandDrivers: ['Nightlife', 'Restaurants', 'Events'],
      priceMultiplier: 2.2,
      trend: 'increasing',
      category: 'entertainment'
    }
  ];

  const eventImpacts: EventImpact[] = [
    {
      id: '1',
      name: 'Lagos Business Summit 2024',
      type: 'conference',
      date: '2024-01-20T09:00:00Z',
      location: 'Eko Hotel & Suites',
      expectedAttendance: 2500,
      demandImpact: 180,
      priceImpact: 25,
      durationHours: 8,
      status: 'upcoming',
      historicalData: {
        previousImpact: 165,
        accuracy: 91.2
      }
    },
    {
      id: '2',
      name: 'Heavy Rainfall Alert',
      type: 'weather',
      date: '2024-01-18T14:00:00Z',
      location: 'Lagos Metropolitan Area',
      expectedAttendance: 0,
      demandImpact: 340,
      priceImpact: 45,
      durationHours: 6,
      status: 'active',
      historicalData: {
        previousImpact: 298,
        accuracy: 87.5
      }
    },
    {
      id: '3',
      name: 'Wizkid Concert',
      type: 'concert',
      date: '2024-01-25T20:00:00Z',
      location: 'Tafawa Balewa Square',
      expectedAttendance: 15000,
      demandImpact: 520,
      priceImpact: 65,
      durationHours: 5,
      status: 'upcoming'
    }
  ];

  const pricingRecommendations: PricingRecommendation[] = [
    {
      id: '1',
      area: 'Lagos Island',
      currentPrice: 1200,
      recommendedPrice: 1440,
      confidence: 0.89,
      reasoning: [
        'High demand during business hours',
        'Limited supply availability',
        'Weather-induced surge expected'
      ],
      expectedImpact: {
        demandChange: -8.5,
        revenueChange: 15.2,
        utilizationChange: 12.3
      },
      validUntil: '2024-01-18T18:00:00Z',
      status: 'active'
    },
    {
      id: '2',
      area: 'Victoria Island',
      currentPrice: 1000,
      recommendedPrice: 1300,
      confidence: 0.92,
      reasoning: [
        'Evening entertainment peak',
        'Concert event impact',
        'Optimal revenue point identified'
      ],
      expectedImpact: {
        demandChange: -5.2,
        revenueChange: 22.8,
        utilizationChange: 8.7
      },
      validUntil: '2024-01-25T23:00:00Z',
      status: 'active'
    }
  ];

  const supplyOptimizations: SupplyOptimization[] = [
    {
      id: '1',
      area: 'Airport Route',
      currentSupply: 95,
      optimalSupply: 140,
      gap: -45,
      gapType: 'shortage',
      recommendations: [
        'Deploy 45 additional vehicles during peak hours',
        'Incentivize drivers with 15% bonus',
        'Implement dynamic routing from nearby areas'
      ],
      impact: {
        waitTimeReduction: 35.8,
        utilizationImprovement: 22.4,
        revenueIncrease: 18.7
      },
      priority: 'high'
    },
    {
      id: '2',
      area: 'Residential Zones',
      currentSupply: 220,
      optimalSupply: 180,
      gap: 40,
      gapType: 'oversupply',
      recommendations: [
        'Redirect 40 vehicles to high-demand areas',
        'Reduce idle time incentives',
        'Optimize routing algorithms'
      ],
      impact: {
        waitTimeReduction: 12.3,
        utilizationImprovement: 28.9,
        revenueIncrease: 14.2
      },
      priority: 'medium'
    }
  ];

  const marketInsights: MarketInsight[] = [
    {
      id: '1',
      title: 'Weekend Demand Pattern Shift',
      category: 'demand-pattern',
      insight: 'Weekend evening demand has shifted 2 hours later, now peaking at 22:00 instead of 20:00',
      impact: 'medium',
      actionable: true,
      data: {
        metric: 'Peak Hour Shift',
        value: 2.0,
        change: 15.3
      },
      recommendations: [
        'Adjust driver shift patterns',
        'Update surge pricing timing',
        'Modify promotional campaigns'
      ],
      confidence: 0.84
    },
    {
      id: '2',
      title: 'Weather Sensitivity Increased',
      category: 'behavioral',
      insight: 'Users are 40% more likely to request rides during light rain compared to 6 months ago',
      impact: 'high',
      actionable: true,
      data: {
        metric: 'Weather Sensitivity',
        value: 40.2,
        change: 23.7
      },
      recommendations: [
        'Implement weather-based pricing',
        'Pre-position vehicles during weather alerts',
        'Develop rain-specific marketing'
      ],
      confidence: 0.91
    }
  ];

  const hourlyDemandData = [
    { hour: '00', demand: 120, supply: 180, utilization: 67 },
    { hour: '04', demand: 80, supply: 120, utilization: 67 },
    { hour: '08', demand: 340, supply: 280, utilization: 121 },
    { hour: '12', demand: 220, supply: 240, utilization: 92 },
    { hour: '16', demand: 280, supply: 260, utilization: 108 },
    { hour: '20', demand: 380, supply: 300, utilization: 127 },
  ];

  const demandByCategory = [
    { name: 'Business', value: 35, color: '#2196F3' },
    { name: 'Airport', value: 25, color: '#4CAF50' },
    { name: 'Entertainment', value: 20, color: '#FF9800' },
    { name: 'Shopping', value: 12, color: '#9C27B0' },
    { name: 'Other', value: 8, color: '#607D8B' },
  ];

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'high': return '#F44336';
      case 'normal': return '#4CAF50';
      case 'low': return '#FF9800';
      default: return '#9E9E9E';
    }
  };

  const getTrendColor = (trend: string): string => {
    switch (trend) {
      case 'increasing': return '#4CAF50';
      case 'decreasing': return '#F44336';
      case 'stable': return '#FF9800';
      default: return '#9E9E9E';
    }
  };

  const getGapTypeColor = (gapType: string): string => {
    switch (gapType) {
      case 'shortage': return '#F44336';
      case 'oversupply': return '#FF9800';
      case 'balanced': return '#4CAF50';
      default: return '#9E9E9E';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'airport': return <Flight />;
      case 'business': return <Business />;
      case 'entertainment': return <MusicNote />;
      case 'shopping': return <ShoppingCart />;
      case 'transport': return <Train />;
      case 'residential': return <LocationOn />;
      default: return <LocationOn />;
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Demand Intelligence
          </Typography>
          <Typography variant="body1" color="text.secondary">
            AI-powered demand forecasting and market optimization
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={<Switch checked={realTimeUpdates} onChange={(e) => setRealTimeUpdates(e.target.checked)} />}
            label="Real-time"
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
              <MenuItem value="1h">Last Hour</MenuItem>
              <MenuItem value="24h">Last 24h</MenuItem>
              <MenuItem value="7d">Last 7 days</MenuItem>
              <MenuItem value="30d">Last 30 days</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Region</InputLabel>
            <Select value={selectedRegion} onChange={(e) => setSelectedRegion(e.target.value)}>
              <MenuItem value="all">All Regions</MenuItem>
              <MenuItem value="lagos">Lagos</MenuItem>
              <MenuItem value="abuja">Abuja</MenuItem>
              <MenuItem value="portharcourt">Port Harcourt</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<Refresh />}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<Insights />}>
            Generate Report
          </Button>
        </Box>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {demandMetrics.map((metric) => (
          <Grid item xs={12} sm={6} md={3} key={metric.id}>
            <Card sx={{ 
              borderLeft: `4px solid ${getStatusColor(metric.status)}`,
              height: '100%'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    p: 1, 
                    borderRadius: 1, 
                    backgroundColor: metric.color + '20',
                    color: metric.color,
                    mr: 2
                  }}>
                    {metric.icon}
                  </Box>
                  <Typography variant="h6" component="div">
                    {metric.title}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
                  <Typography variant="h4" component="div" sx={{ mr: 1 }}>
                    {typeof metric.currentValue === 'number' ? 
                      (metric.unit === 'NGN' ? formatCurrency(metric.currentValue) : 
                       metric.currentValue.toLocaleString()) : 
                      metric.currentValue}
                  </Typography>
                  {metric.unit !== 'NGN' && (
                    <Typography variant="body2" color="text.secondary">
                      {metric.unit}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {metric.changeDirection === 'up' ? (
                      <TrendingUp sx={{ color: metric.status === 'high' && metric.id === 'avg-wait-time' ? '#F44336' : '#4CAF50', fontSize: 16 }} />
                    ) : (
                      <TrendingDown sx={{ color: metric.status === 'low' ? '#F44336' : '#4CAF50', fontSize: 16 }} />
                    )}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: metric.changeDirection === 'up' 
                          ? (metric.status === 'high' && metric.id === 'avg-wait-time' ? '#F44336' : '#4CAF50')
                          : (metric.status === 'low' ? '#F44336' : '#4CAF50'),
                        ml: 0.5
                      }}
                    >
                      {metric.change > 0 ? '+' : ''}{metric.change}%
                    </Typography>
                  </Box>
                  <Chip
                    label={metric.status}
                    size="small"
                    sx={{
                      backgroundColor: getStatusColor(metric.status) + '20',
                      color: getStatusColor(metric.status),
                      textTransform: 'capitalize'
                    }}
                  />
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Forecast: {typeof metric.forecast === 'number' ? 
                      (metric.unit === 'NGN' ? formatCurrency(metric.forecast) : 
                       metric.forecast.toLocaleString() + (metric.unit !== 'NGN' ? ' ' + metric.unit : '')) : 
                      metric.forecast}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={metric.confidence * 100}
                      sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {(metric.confidence * 100).toFixed(0)}% confidence
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* AI Insights Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PsychologyAlt />
            AI Insights Ready
          </Box>
        </AlertTitle>
        Demand forecast shows 23% increase expected in next 2 hours due to weather patterns. Consider implementing surge pricing.
        <Button size="small" variant="outlined" sx={{ ml: 2 }}>
          View Recommendations
        </Button>
      </Alert>

      {/* Tabs Navigation */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<ShowChart />} label="Demand Forecast" />
          <Tab icon={<Map />} label="Hotspot Analysis" />
          <Tab icon={<AttachMoney />} label="Pricing Intelligence" />
          <Tab icon={<Speed />} label="Supply Optimization" />
          <Tab icon={<Event />} label="Event Impact" />
          <Tab icon={<PsychologyAlt />} label="Market Insights" />
          <Tab icon={<Analytics />} label="Performance Analytics" />
        </Tabs>
      </Card>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <Grid container spacing={3}>
          {/* Demand Forecast Chart */}
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    24-Hour Demand Forecast
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2">Forecast Horizon:</Typography>
                    <Slider
                      value={forecastHorizon}
                      onChange={(e, value) => setForecastHorizon(value as number)}
                      min={1}
                      max={168}
                      step={1}
                      sx={{ width: 100 }}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value}h`}
                    />
                  </Box>
                </Box>
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={demandForecastData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="timestamp" />
                    <YAxis />
                    <RechartsTooltip />
                    <Area dataKey="confidence_upper" fill="#E3F2FD" stroke="none" />
                    <Area dataKey="confidence_lower" fill="#FFFFFF" stroke="none" />
                    <Line type="monotone" dataKey="actual" stroke="#2196F3" strokeWidth={3} name="Actual Demand" />
                    <Line type="monotone" dataKey="predicted" stroke="#FF9800" strokeWidth={2} strokeDasharray="5 5" name="Predicted" />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Hourly Breakdown */}
          <Grid item xs={12} lg={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Supply vs Demand
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <RechartsBarChart data={hourlyDemandData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="demand" fill="#2196F3" name="Demand" />
                    <Bar dataKey="supply" fill="#4CAF50" name="Supply" />
                  </RechartsBarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {selectedTab === 1 && (
        <Grid container spacing={3}>
          {hotspotAreas.map((hotspot) => (
            <Grid item xs={12} md={6} lg={4} key={hotspot.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{ color: 'primary.main' }}>
                      {getCategoryIcon(hotspot.category)}
                    </Box>
                    <Typography variant="h6" component="div">
                      {hotspot.name}
                    </Typography>
                    <Chip
                      label={hotspot.trend}
                      size="small"
                      sx={{
                        backgroundColor: getTrendColor(hotspot.trend) + '20',
                        color: getTrendColor(hotspot.trend),
                        textTransform: 'capitalize'
                      }}
                    />
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Current Demand</Typography>
                      <Typography variant="h6">{hotspot.currentDemand}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Predicted</Typography>
                      <Typography variant="h6" color="primary.main">
                        {hotspot.predictedDemand}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Supply Available</Typography>
                      <Typography variant="h6">{hotspot.supplyAvailable}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Utilization</Typography>
                      <Typography variant="h6">{hotspot.utilizationRate}%</Typography>
                    </Grid>
                  </Grid>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Average Wait Time
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min((hotspot.averageWaitTime / 10) * 100, 100)}
                        sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                        color={hotspot.averageWaitTime > 5 ? 'error' : hotspot.averageWaitTime > 3 ? 'warning' : 'success'}
                      />
                      <Typography variant="body2">
                        {hotspot.averageWaitTime} min
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Peak Hours
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {hotspot.peakHours.map((hour, index) => (
                        <Chip key={index} label={hour} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Demand Drivers
                    </Typography>
                    <Typography variant="body2">
                      {hotspot.demandDrivers.join(', ')}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Price Multiplier: {hotspot.priceMultiplier}x
                    </Typography>
                    <Button size="small" variant="outlined">
                      Optimize
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {selectedTab === 2 && (
        <Grid container spacing={3}>
          {pricingRecommendations.map((recommendation) => (
            <Grid item xs={12} md={6} key={recommendation.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" component="div">
                        {recommendation.area}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Valid until {formatDate(recommendation.validUntil)}
                      </Typography>
                    </Box>
                    <Chip
                      label={recommendation.status}
                      size="small"
                      color={recommendation.status === 'active' ? 'primary' : recommendation.status === 'implemented' ? 'success' : 'default'}
                    />
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Current Price</Typography>
                      <Typography variant="h6">{formatCurrency(recommendation.currentPrice)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Recommended</Typography>
                      <Typography variant="h6" color="primary.main">
                        {formatCurrency(recommendation.recommendedPrice)}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Confidence Level
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={recommendation.confidence * 100}
                        sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                      />
                      <Typography variant="body2">
                        {(recommendation.confidence * 100).toFixed(0)}%
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Reasoning
                    </Typography>
                    <List dense>
                      {recommendation.reasoning.map((reason, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            <Box sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: 'text.secondary' }} />
                          </ListItemIcon>
                          <ListItemText primary={reason} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Expected Impact
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Revenue</Typography>
                        <Typography variant="body2" color="success.main">
                          +{recommendation.expectedImpact.revenueChange}%
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Demand</Typography>
                        <Typography variant="body2" color="error.main">
                          {recommendation.expectedImpact.demandChange}%
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Utilization</Typography>
                        <Typography variant="body2" color="success.main">
                          +{recommendation.expectedImpact.utilizationChange}%
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" fullWidth>
                      Implement
                    </Button>
                    <Button size="small" variant="outlined" fullWidth>
                      Modify
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {selectedTab === 3 && (
        <Grid container spacing={3}>
          {supplyOptimizations.map((optimization) => (
            <Grid item xs={12} md={6} key={optimization.id}>
              <Card sx={{
                borderLeft: `4px solid ${getGapTypeColor(optimization.gapType)}`
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" component="div">
                        {optimization.area}
                      </Typography>
                      <Chip
                        label={optimization.gapType}
                        size="small"
                        sx={{
                          backgroundColor: getGapTypeColor(optimization.gapType) + '20',
                          color: getGapTypeColor(optimization.gapType),
                          textTransform: 'capitalize'
                        }}
                      />
                    </Box>
                    <Chip
                      label={optimization.priority}
                      size="small"
                      color={optimization.priority === 'high' ? 'error' : optimization.priority === 'medium' ? 'warning' : 'success'}
                    />
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Current</Typography>
                      <Typography variant="h6">{optimization.currentSupply}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Optimal</Typography>
                      <Typography variant="h6" color="primary.main">
                        {optimization.optimalSupply}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Gap</Typography>
                      <Typography variant="h6" color={optimization.gap < 0 ? 'error.main' : 'warning.main'}>
                        {optimization.gap > 0 ? '+' : ''}{optimization.gap}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Recommendations
                    </Typography>
                    <List dense>
                      {optimization.recommendations.map((rec, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                          </ListItemIcon>
                          <ListItemText primary={rec} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Expected Impact
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Wait Time</Typography>
                        <Typography variant="body2" color="success.main">
                          -{optimization.impact.waitTimeReduction}%
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Utilization</Typography>
                        <Typography variant="body2" color="success.main">
                          +{optimization.impact.utilizationImprovement}%
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Revenue</Typography>
                        <Typography variant="body2" color="success.main">
                          +{optimization.impact.revenueIncrease}%
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Button variant="contained" fullWidth size="small">
                    Apply Optimization
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {selectedTab === 4 && (
        <Grid container spacing={3}>
          {eventImpacts.map((event) => (
            <Grid item xs={12} md={6} lg={4} key={event.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{ color: 'primary.main' }}>
                      {event.type === 'sports' ? <SportsSoccer /> :
                       event.type === 'concert' ? <MusicNote /> :
                       event.type === 'conference' ? <Business /> :
                       event.type === 'weather' ? <CloudQueue /> :
                       event.type === 'holiday' ? <Event /> : <DirectionsBus />}
                    </Box>
                    <Typography variant="h6" component="div">
                      {event.name}
                    </Typography>
                    <Chip
                      label={event.status}
                      size="small"
                      color={event.status === 'active' ? 'success' : event.status === 'upcoming' ? 'warning' : 'default'}
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {formatDate(event.date)} • {event.location}
                  </Typography>

                  {event.expectedAttendance > 0 && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Expected Attendance: {event.expectedAttendance.toLocaleString()}
                    </Typography>
                  )}

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Demand Impact</Typography>
                      <Typography variant="h6" color="primary.main">
                        +{event.demandImpact}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Price Impact</Typography>
                      <Typography variant="h6" color="success.main">
                        +{event.priceImpact}%
                      </Typography>
                    </Grid>
                  </Grid>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Duration: {event.durationHours} hours
                  </Typography>

                  {event.historicalData && (
                    <Box sx={{ mt: 2, p: 1, backgroundColor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Previous Event: {event.historicalData.previousImpact} impact • {event.historicalData.accuracy}% accuracy
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mt: 2 }}>
                    <Button size="small" variant="outlined" fullWidth>
                      View Preparation Plan
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {selectedTab === 5 && (
        <Grid container spacing={3}>
          {marketInsights.map((insight) => (
            <Grid item xs={12} md={6} key={insight.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" component="div">
                        {insight.title}
                      </Typography>
                      <Chip
                        label={insight.category}
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip
                        label={insight.impact}
                        size="small"
                        color={insight.impact === 'high' ? 'error' : insight.impact === 'medium' ? 'warning' : 'success'}
                      />
                      {insight.actionable && (
                        <Chip label="Actionable" size="small" color="primary" />
                      )}
                    </Box>
                  </Box>

                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {insight.insight}
                  </Typography>

                  <Box sx={{ mb: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {insight.data.metric}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6">
                        {insight.data.value}%
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TrendingUp sx={{ color: '#4CAF50', fontSize: 16 }} />
                        <Typography variant="body2" sx={{ color: '#4CAF50', ml: 0.5 }}>
                          +{insight.data.change}%
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Confidence Level
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={insight.confidence * 100}
                        sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                      />
                      <Typography variant="body2">
                        {(insight.confidence * 100).toFixed(0)}%
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Recommendations
                    </Typography>
                    <List dense>
                      {insight.recommendations.map((rec, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            <Lightbulb sx={{ fontSize: 16, color: 'warning.main' }} />
                          </ListItemIcon>
                          <ListItemText primary={rec} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  <Button variant="outlined" fullWidth size="small">
                    Apply Insights
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {selectedTab === 6 && (
        <Grid container spacing={3}>
          {/* Demand by Category */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Demand by Category
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPieChart>
                    <Pie
                      dataKey="value"
                      data={demandByCategory}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, value }) => `${name}: ${value}%`}
                    >
                      {demandByCategory.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Performance Metrics */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Key Performance Indicators
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Forecast Accuracy"
                      secondary="Last 30 days average"
                    />
                    <ListItemSecondaryAction>
                      <Typography variant="h6" color="success.main">
                        89.2%
                      </Typography>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Revenue Optimization"
                      secondary="Pricing recommendations impact"
                    />
                    <ListItemSecondaryAction>
                      <Typography variant="h6" color="primary.main">
                        +18.7%
                      </Typography>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Wait Time Reduction"
                      secondary="Supply optimization impact"
                    />
                    <ListItemSecondaryAction>
                      <Typography variant="h6" color="success.main">
                        -24.3%
                      </Typography>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Demand Prediction Range"
                      secondary="Next 24 hours confidence"
                    />
                    <ListItemSecondaryAction>
                      <Typography variant="h6">
                        ±12%
                      </Typography>
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default DemandIntelligencePage;